import { get_token } from "@/router";

const platform_switch_url = process.env.VUE_APP_PLATFORM_SWITCH_URL;
const requested_platform_code = (process.env.VUE_APP_REQUESTED_PLATFORM_CODES || "").trim();

function format_platform_label(code) {
  return code
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const fallback_platforms = [
  {
    id: "dbe_dashboard",
    label: "Siyanda Platinum Platform",
    requested_platform_code: "dbe_dashboard",
    description: "Open Siyanda Platinum Platform",
    icon: "bi bi-grid"
  }
];

const configured_platform = requested_platform_code
  ? {
      id: requested_platform_code,
      label: format_platform_label(requested_platform_code),
      requested_platform_code,
      description: `Open ${format_platform_label(requested_platform_code)}`,
      icon: "bi bi-grid"
    }
  : null;

const platforms = configured_platform ? [configured_platform] : fallback_platforms;
const blocked_popup_error_message = "Your browser blocked the new platform tab. Please allow pop-ups and try again.";

async function parse_response_body(response) {
  const content_type = response.headers.get("content-type") || "";

  if (content_type.includes("application/json")) {
    return response.json().catch(() => ({}));
  }

  const message = await response.text().catch(() => "");
  return message ? { message } : {};
}

function require_value(value, message) {
  if (!value) {
    throw new Error(message);
  }

  return value;
}

function get_response_error(data) {
  return data.error || data.message || "Could not switch platform.";
}

function get_redirect_url(data) {
  return data.redirect_url || data.url;
}

function open_platform_tab() {
  const opened_window = window.open("about:blank", "_blank");

  if (!opened_window) {
    throw new Error(blocked_popup_error_message);
  }

  opened_window.opener = null;
  return opened_window;
}

function redirect_platform_tab(opened_window, redirect_url) {
  opened_window.location.href = redirect_url;
}

async function request_platform_redirect(platform) {
  const auth_token = require_value(
    get_token(),
    "Your session could not be found. Please sign in again."
  );
  const switch_url = require_value(
    platform.switch_url || platform_switch_url,
    "Platform switch URL is not configured."
  );
  const response = await fetch(switch_url, {
    method: "POST",
    headers: {
      Authorization: `Token ${auth_token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      requested_platform_code: platform.requested_platform_code || platform.id
    })
  });
  const data = await parse_response_body(response);

  if (!response.ok) {
    throw new Error(get_response_error(data));
  }

  return require_value(
    get_redirect_url(data),
    "Platform response did not include a redirect URL."
  );
}

export default {
  name: "PlatformSwitcher",

  data() {
    return ({
      error_message: "",
      is_loading: null,
      platforms
    });
  },

  computed: {
    primary_platform() {
      return this.platforms.find((platform) => !platform.disabled) || this.platforms[0] || null;
    },

    is_switching() {
      return Boolean(this.is_loading);
    }
  },

  methods: {
    switch_to_primary_platform() {
      return this.redirect_to_platform(this.primary_platform);
    },

    async redirect_to_platform(platform) {
      if (!platform || platform.disabled) {
        return;
      }

      this.error_message = "";
      this.is_loading = platform.id;
      let platform_tab = null;

      try {
        platform_tab = open_platform_tab();
        const redirect_url = await request_platform_redirect(platform);
        redirect_platform_tab(platform_tab, redirect_url);
      } catch (error) {
        platform_tab?.close();
        this.error_message = (error.message || "").includes(blocked_popup_error_message)
          ? ""
          : error.message || "Could not switch platform.";
      } finally {
        this.is_loading = null;
      }
    }
  }
};
